import {
  InterfacePlugin,
  type InterfacePluginContext,
  type JobProgressEvent,
  type JobContext,
} from "@brains/plugins";
import type { Daemon, DaemonHealth } from "@brains/plugins";
import { ServerManager } from "./server-manager";
import { existsSync } from "fs";
import { join } from "path";
import {
  webserverConfigSchema,
  type WebserverConfig,
  type WebserverConfigInput,
} from "./config";
import { placeholderHtml } from "./templates/placeholder";
import packageJson from "../package.json";

/**
 * Webserver interface for serving static sites, shared web routes, and API routes.
 *
 * Runs in-process via Bun.serve() on the shared host.
 */
export class WebserverInterface extends InterfacePlugin<
  WebserverConfig,
  WebserverConfigInput
> {
  private serverManager?: ServerManager;
  private siteUrl: string | undefined;
  private previewUrl: string | undefined;

  constructor(config: WebserverConfigInput = {}) {
    super("webserver", packageJson, config, webserverConfigSchema);
  }

  protected override async onRegister(
    context: InterfacePluginContext,
  ): Promise<void> {
    this.siteUrl = context.siteUrl;
    this.previewUrl = context.previewUrl;

    // Advertise the site + preview URLs through the endpoint registry so
    // the dashboard can render them in its Endpoints card. Local dev
    // without a configured domain has no absolute URL to advertise —
    // skip Site in that case; the dashboard itself is the operator's
    // landing page.
    if (this.siteUrl) {
      context.endpoints.register({
        label: "Site",
        url: this.siteUrl,
        priority: 10,
      });
      context.interactions.register({
        id: "site",
        label: "Public site",
        description: "Visit the published public site for this brain.",
        href: this.siteUrl,
        kind: "human",
        priority: 10,
      });
    }
    if (this.config.enablePreview && this.previewUrl) {
      context.endpoints.register({
        label: "Preview",
        url: this.previewUrl,
        priority: 20,
        visibility: "admin",
      });
      context.interactions.register({
        id: "preview",
        label: "Preview site",
        description: "Open the private preview build.",
        href: this.previewUrl,
        kind: "admin",
        priority: 20,
        visibility: "admin",
      });
    }

    // Initialize server manager for the shared host
    this.serverManager = new ServerManager({
      logger: context.logger,
      productionDistDir: this.config.productionDistDir,
      sharedImagesDir: this.config.sharedImagesDir,
      productionPort: this.config.productionPort,
      ...(this.config.hostname ? { hostname: this.config.hostname } : {}),
      ...(this.config.enablePreview &&
        this.config.previewDistDir && {
          previewDistDir: this.config.previewDistDir,
        }),
      getOperationalInfo: (): Promise<
        Awaited<ReturnType<typeof context.appInfo>>
      > => context.appInfo(),
      getReadinessData: (): ReturnType<typeof context.readiness> =>
        context.readiness(),
      getRoutes: (): ReturnType<typeof context.httpRoutes.getRoutes> =>
        context.httpRoutes.getRoutes(),
      messageBus: context.apiRoutes.getMessageBus(),
    });
  }

  private getServerManager(): ServerManager {
    if (!this.serverManager) {
      throw new Error("ServerManager not initialized — onRegister not called");
    }
    return this.serverManager;
  }

  public override requiresDaemonStartup(): boolean {
    return true;
  }

  protected override createDaemon(): Daemon | undefined {
    return {
      start: async (): Promise<void> => {
        // Ensure dist directories exist with placeholder content
        await this.ensureDistDirectories();

        await this.getServerManager().start();
      },
      stop: async (): Promise<void> => {
        await this.serverManager?.stop();
      },
      healthCheck: async (): Promise<DaemonHealth> => {
        const status = this.serverManager?.getStatus();
        const isRunning = status?.running ?? false;

        const productionUrl =
          this.siteUrl ??
          status?.productionUrl ??
          `http://localhost:${this.config.productionPort}`;
        const previewUrl =
          this.previewUrl ??
          status?.previewUrl ??
          `http://localhost:${this.config.previewPort}`;

        const urls: string[] = [];
        if (isRunning) {
          urls.push(`Production: ${productionUrl}`);
          if (status?.previewUrl) {
            urls.push(`Preview: ${previewUrl}`);
          }
        }

        return {
          status: isRunning ? "healthy" : "error",
          message: isRunning ? urls.join(", ") : "Webserver not running",
          lastCheck: new Date(),
          details: {
            preview: !!status?.previewUrl,
            production: isRunning,
            previewUrl: status?.previewUrl ? previewUrl : undefined,
            productionUrl: isRunning ? productionUrl : undefined,
          },
        };
      },
    };
  }

  protected override async handleProgressEvent(
    _event: JobProgressEvent,
    _context: JobContext,
  ): Promise<void> {
    // Webserver doesn't need to handle progress events
  }

  private async ensureDistDirectories(): Promise<void> {
    const { mkdir, writeFile } = await import("fs/promises");

    if (
      this.config.enablePreview &&
      this.config.previewDistDir &&
      !existsSync(this.config.previewDistDir)
    ) {
      await mkdir(this.config.previewDistDir, { recursive: true });
      await writeFile(
        join(this.config.previewDistDir, "index.html"),
        placeholderHtml,
      );
      this.logger.debug(
        `Created preview directory at ${this.config.previewDistDir}`,
      );
    }

    if (!existsSync(this.config.productionDistDir)) {
      await mkdir(this.config.productionDistDir, { recursive: true });
      await writeFile(
        join(this.config.productionDistDir, "index.html"),
        placeholderHtml,
      );
      this.logger.debug(
        `Created production directory at ${this.config.productionDistDir}`,
      );
    }
  }
}
